/* src/GeminiService.js - Client-Side AI Moderation */
import { GoogleGenerativeAI } from "@google/generative-ai";
import { db } from "./db";
import firebase from "firebase";

// --- 1. SAVE SETTINGS (Call this when Admin clicks "Save") ---
export const saveAgentSettings = async (userId, apiKey, profile, flags, ageGroup, additionalInstructions, isEnabled) => {
  try {
    // Get existing data first to preserve fields
    const existingDoc = await db.collection("AgentConfiguration").doc(userId).get();
    const existingData = existingDoc.exists ? existingDoc.data() : {};

    // Build update object - only update provided fields
    const updateData = {
      updated_at: firebase.firestore.FieldValue.serverTimestamp()
    };

    if (apiKey) updateData.api_key = apiKey; // Only update if provided
    if (profile) updateData.sensitivity_profile = profile;
    if (flags && flags.length > 0) updateData.custom_flags = flags;
    if (ageGroup) updateData.age_group = ageGroup;
    if (additionalInstructions !== undefined) updateData.additional_instructions = additionalInstructions;
    if (isEnabled !== undefined) updateData.is_enabled = isEnabled;

    // Merge with existing or set defaults for new
    if (!existingDoc.exists) {
      updateData.created_at = firebase.firestore.FieldValue.serverTimestamp();
      updateData.usage_count = 0;
      updateData.surveys_flagged = 0;
      updateData.api_key = updateData.api_key || '';
      updateData.sensitivity_profile = updateData.sensitivity_profile || 'Balanced';
      updateData.age_group = updateData.age_group || '13+';
      updateData.is_enabled = updateData.is_enabled !== undefined ? updateData.is_enabled : true;
    }

    await db.collection("AgentConfiguration").doc(userId).set(
      updateData,
      { merge: true } // Merge with existing data
    );

    console.log("✅ AI Agent Settings Saved!");
    return { success: true };
  } catch (error) {
    console.error("❌ Save Error:", error);
    return { success: false, error: error.message };
  }
};

// --- 2. GET SETTINGS ---
export const getAgentSettings = async (userId) => {
  try {
    const doc = await db.collection("AgentConfiguration").doc(userId).get();
    if (!doc.exists) {
      return { success: false, error: "No AI agent configured" };
    }
    return { success: true, data: doc.data() };
  } catch (error) {
    console.error("❌ Get Settings Error:", error);
    return { success: false, error: error.message };
  }
};

// --- 3. CHECK SURVEY (Call this when User clicks "Publish") ---
export const checkSurvey = async (userId, surveyName, questions) => {
  try {
    // A. Get the Key and Settings from Database
    console.log("🔍 Checking for AI agent with userId:", userId);
    let doc = await db.collection("AgentConfiguration").doc(userId).get();
    console.log("🔍 Document exists:", doc.exists);

    // If not found, try to find ANY enabled agent
    if (!doc.exists) {
      console.log("🔍 Trying to find any enabled agent...");
      const querySnapshot = await db.collection("AgentConfiguration")
        .where("is_enabled", "==", true)
        .limit(1)
        .get();

      if (querySnapshot.empty) {
        console.log("⚠️ No AI agent configured, skipping AI moderation");
        return { status: "SAFE", reason: "No AI agent configured" };
      }

      doc = querySnapshot.docs[0];
      console.log("✅ Found agent with ID:", doc.id);
    }

    const settings = doc.data();

    // Check if AI moderation is enabled
    if (settings.is_enabled === false) {
      console.log("⚠️ AI moderation is disabled, skipping");
      return { status: "SAFE", reason: "AI moderation disabled" };
    }

    // B. Build system instruction
    let systemInstruction = `You are a content moderation AI for gaming surveys.

CRITICAL RULE - USE SENSIBLE JUDGMENT:
These are SAFE (demographic/research questions):
- "Sex: Male / Female / Other" ← This is asking gender, NOT explicit content
- "Age: 13-18, 19-25, 26+" ← This is demographic data
- "What games do you play?" ← Research question

These are UNSAFE and must be FLAGGED:
- ANY explicit sexual content, requests, or references (e.g., "send nudes", "sexual acts", etc.)
- Requests for nude photos or explicit images
- Sexual harassment or solicitation
- Explicit sexual descriptions or scenarios
- Personal identifying info (address, SSN, phone numbers)
- Hate speech, slurs, or discriminatory language
- Illegal activities or encouragement of illegal behavior
- Self-harm or violence against others
- Drug use or illegal substances

IMPORTANT: If a question contains phrases like "send nudes", "sex chat", "explicit photos", or similar sexual solicitation, it MUST be flagged regardless of context.

SENSITIVITY: ${settings.sensitivity_profile}
AGE GROUP: ${settings.age_group}
`;

    if (settings.custom_flags && settings.custom_flags.length > 0) {
      systemInstruction += `\nCUSTOM FLAGS - Strictly flag if you see these:\n`;
      settings.custom_flags.forEach(flag => {
        systemInstruction += `- ${flag}\n`;
      });
    }

    if (settings.additional_instructions) {
      systemInstruction += `\nADDITIONAL INSTRUCTIONS: \n Index starts at 0.\n${settings.additional_instructions}\n`;
    } else {
      systemInstruction += `\nADDITIONAL INSTRUCTIONS: Index starts at 0.\n`;
    }

    systemInstruction += `\nOUTPUT FORMAT:
Return ONLY valid JSON (no markdown):
{
  "status": "SAFE" or "FLAGGED",
  "flagged_questions": [{"index": 0, "reason": "..."}],
  "overall_reason": "..."
}`;

    // C. Format survey for Gemini
    let surveyText = `Survey: ${surveyName}\n\n`;
    questions.forEach((q, index) => {
      surveyText += `Question ${index + 1} (${q.qType}):\n`;
      surveyText += `  Title: ${q.content.title}\n`;

      if (q.qType === 'multi-choice' || q.qType === 'multi-select') {
        if (q.content.options && Array.isArray(q.content.options)) {
          surveyText += `  Options: ${q.content.options.map(o => o.text || o).join(', ')}\n`;
        }
      } else if (q.qType === 'dropdown') {
        if (q.content.options && Array.isArray(q.content.options)) {
          surveyText += `  Options: ${q.content.options.join(', ')}\n`;
        }
      } else if (q.qType === 'linear-scale') {
        surveyText += `  Scale: ${q.content.start}-${q.content.end}\n`;
        surveyText += `  Labels: "${q.content.startLabel}" to "${q.content.endLabel}"\n`;
      }
      surveyText += `\n`;
    });

    // D. Call Gemini
    console.log("🤖 Calling Gemini for AI moderation...");
    const genAI = new GoogleGenerativeAI(settings.api_key);
    const model = genAI.getGenerativeModel({
      model: "gemini-2.0-flash-exp",
      systemInstruction: systemInstruction,
      generationConfig: {
        temperature: 0.1,
        topK: 1,
        topP: 0.8,
        maxOutputTokens: 2048,
      }
    });

    const result = await model.generateContent(`Moderate this survey:\n\n${surveyText}`);
    const response = result.response.text();

    console.log("🤖 Gemini response:", response);

    // E. Parse response
    const cleanJson = response.replace(/```json|```/g, "").trim();
    const moderationResult = JSON.parse(cleanJson);

    // F. Update stats (use doc.id in case we found a different agent)
    await db.collection("AgentConfiguration").doc(doc.id).update({
      usage_count: firebase.firestore.FieldValue.increment(1),
      surveys_flagged: firebase.firestore.FieldValue.increment(
        moderationResult.status === 'FLAGGED' ? 1 : 0
      ),
      last_used_at: firebase.firestore.FieldValue.serverTimestamp()
    });

    return moderationResult;

  } catch (error) {
    console.error("❌ Gemini Error:", error);
    // Graceful fallback - don't block survey
    return { status: "SAFE", reason: "AI moderation unavailable: " + error.message };
  }
};

// --- 4. DELETE AGENT ---
export const deleteAgentSettings = async (userId) => {
  try {
    await db.collection("AgentConfiguration").doc(userId).delete();
    return { success: true };
  } catch (error) {
    console.error("❌ Delete Error:", error);
    return { success: false, error: error.message };
  }
};



// WEBPACK FOOTER //
// ./src/GeminiService.js